'use client';

import { whatsapp, telegram, vk } from 'vanilla-sharing';
import TelegramSVG from '@/svg/telegram.svg';
import VkSVG from '@/svg/vk.svg';
import WhatsAppSVG from '@/svg/whatsapp.svg';
import ShareSVG from '@/svg/share.svg?url';
import { m as motion, AnimatePresence } from 'framer-motion';
import { HTMLAttributes, useEffect, useRef, useState, ElementType } from 'react';
import { useOnClickOutside } from '@/hooks/use-on-click-outside';
import { useMediaQueryDeviceState } from '@/atoms/media-query-device';
import Button from '../Button';

interface Props extends HTMLAttributes<HTMLElement> {
    /**
     * Использовать ли Web Share API (если доступно)
     */
    native?: boolean;

    /**
     * Шерить ли в Telegram
     */
    telegram?: boolean;

    /**
     * Заголовок для шеринга
     */
    title?: string;

    /**
     * URL, который шерим
     */
    url?: string;

    /**
     * Шерить ли в VK
     */
    vk?: boolean;

    /**
     * Шерить ли в Whatsapp
     */
    whatsapp?: boolean;
}

function shareVK(url: string) {
    vk({
        url,
        title: document.title,
    });
}

function shareWhatsapp(url: string) {
    whatsapp({
        url,
        title: document.title,
    });
}

function shareTelegram(url: string) {
    telegram({
        url,
        title: document.title,
    });
}

const iconsDict: Record<string, ElementType> = {
    telegram: TelegramSVG,
    vk: VkSVG,
    whatsapp: WhatsAppSVG,
};

const callbacksDict: Record<string, any> = {
    telegram: shareTelegram,
    vk: shareVK,
    whatsapp: shareWhatsapp,
};

const WebShare = ({ native = false, telegram = false, title, url, vk = false, whatsapp = false, ...props }: Props) => {
    const activeSocial = [
        { name: 'telegram', value: telegram },
        { name: 'vk', value: vk },
        { name: 'whatsapp', value: whatsapp },
    ].filter((item) => item.value === true);
    const [isOpened, setIsOpened] = useState(false);
    const webShareEl = useRef<HTMLDivElement>(null);
    const [mediaQueryDevice] = useMediaQueryDeviceState();
    const isUseNative = native && 'share' in navigator && mediaQueryDevice !== 'desktop';

    useEffect(() => {
        const onScroll = () => {
            if (isOpened) {
                setIsOpened(false);
            }
        };

        document.addEventListener('scroll', onScroll);
        return () => {
            document.removeEventListener('scroll', onScroll);
        };
    }, [isOpened]);

    useOnClickOutside(webShareEl, () => setIsOpened(false));

    return (
        <div className="web-share" ref={webShareEl}>
            <Button
                variant="bordered-light"
                icon={
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={ShareSVG} alt="" width={24} height={24} className="img-fluid" decoding="async" />
                }
                onClick={async () => {
                    if (isUseNative) {
                        await navigator.share({ text: '', title, url });
                        return;
                    }
                    setIsOpened(true);
                }}
                className="web-share-btn"
            >
                Поделиться
            </Button>
            <AnimatePresence mode="wait">
                {isOpened && (
                    <motion.div className="web-share-inner" key={`inner-${isOpened}`}>
                        <motion.div
                            className="web-share-top"
                            initial={{
                                opacity: 0,
                                y: 10,
                            }}
                            exit={{
                                opacity: 0,
                                y: -10,
                                transition: {
                                    duration: 0.25,
                                    delay: 0,
                                },
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                                transition: {
                                    duration: 0.25,
                                },
                            }}
                        >
                            <div className="web-share-title text-xs">Поделиться</div>
                            <Button
                                geometryVariant="square-bracket"
                                onClick={() => {
                                    setIsOpened(false);
                                }}
                            >
                                x
                            </Button>
                        </motion.div>
                        <motion.ul
                            initial={{
                                opacity: 0,
                                y: 10,
                            }}
                            exit={{
                                opacity: 0,
                                y: -10,
                                transition: {
                                    duration: 0.25,
                                    delay: 0,
                                },
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                                transition: {
                                    duration: 0.25,
                                },
                            }}
                            className="web-share-list list-unstyled"
                        >
                            {activeSocial.map((item, i) => {
                                const Icon = iconsDict[item.name];
                                const callback = callbacksDict[item.name];
                                return (
                                    <motion.li
                                        key={i}
                                        initial={{
                                            opacity: 0,
                                            y: 10,
                                        }}
                                        variants={{
                                            opened: {
                                                opacity: 1,
                                                y: 0,
                                                transition: {
                                                    duration: 0.25,
                                                    delay: 0.3 + i * 0.05,
                                                },
                                            },
                                            closed: {
                                                opacity: 0,
                                                y: -10,
                                                transition: {
                                                    duration: 0.25,
                                                    delay: 0,
                                                },
                                            },
                                        }}
                                        animate={isOpened ? 'opened' : 'closed'}
                                    >
                                        <button
                                            className="web-share-button"
                                            aria-label="Поделиться"
                                            onClick={() => {
                                                if (callback && typeof callback === 'function') {
                                                    callback(url);
                                                }
                                            }}
                                        >
                                            {Icon ? <Icon /> : item.name}
                                        </button>
                                    </motion.li>
                                );
                            })}
                        </motion.ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default WebShare;
